import { format } from 'date-fns';
import { forwardRef } from 'react';
import QRCode from 'react-qr-code';

const numberToWords = (num) => {
   if (num === undefined || num === null) return '';

   const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
      'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
   const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

   if (num < 20) return ones[num];

   if (num < 100) {
      return tens[Math.floor(num / 10)] + (num % 10 ? '-' + ones[num % 10] : '');
   }

   if (num < 1000) {
      return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
   }

   return num.toString();
};

const COVCertificateTemplate = forwardRef(({ certificate, application, orderOfPayment, hiddenOnPrint = [] }, ref) => {
   const certificateData = certificate?.covCertificateData;
   const forestProducts = certificateData?.forestProducts;

   console.log('Certificate Data:', certificateData);
   console.log('Forest Products:', forestProducts);

   // Use a direct URL instead of JSON data for better QR code compatibility across devices
   const qrValue = `${window.location.origin}/verify-certificate/${certificate?.id}`;

   const formatDate = (timestamp) => {
      try {
         if (!timestamp) return null;
         if (typeof timestamp === 'string') {
            if (!isNaN(timestamp)) {
               return format(new Date(parseInt(timestamp)), 'MMMM d, yyyy');
            }
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }
         if (typeof timestamp === 'number') {
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }
         if (timestamp instanceof Date) {
            return format(timestamp, 'MMMM d, yyyy');
         }
         return null;
      } catch (error) {
         console.error('Error formatting date:', error, 'timestamp:', timestamp);
         return null;
      }
   };

   // Function to render signature image
   const renderSignature = (signatureData) => {
      if (!signatureData?.data) return null;
      return (
         <img
            src={`data:${signatureData.contentType};base64,${signatureData.data}`}
            alt="Signature"
            className="h-16 object-contain mb-2"
         />
      );
   };

   return (
      <div ref={ref} className="p-8 bg-white">
         {/* Header */}
         <div className="text-center mb-8">
            <h1 className="text-xl font-bold mb-4">CERTIFICATE OF VERIFICATION</h1>
         </div>

         {/* To all concern section */}
         <div className="mb-6">
            <p className="mb-4">To all concern:</p>
            <p className="text-justify mb-4 indent-8">
               Be informed that I, <span className="font-bold">{certificateData?.name?.toUpperCase() || '_______________'}</span> <span></span>is a holder of <span className="font-bold">Private Land Timber Permit</span> from the <span className="font-bold">DENR MIMAROPA Region</span> bearing control Number <span className="underline font-bold">{certificateData?.permitNumber || '_______________'}</span> issued on <span className="underline font-bold">{formatDate(certificate?.dateIssued) || '_______________'}</span>, to cut/utilize/manufacture <span className="underline font-bold">{numberToWords(forestProducts?.numberOfTrees) || '___'} ({forestProducts?.numberOfTrees || ''}) {forestProducts?.species || '___'} ({forestProducts?.scientificName ? <i>{forestProducts?.scientificName}</i> : ''})</span> trees located at <span className="underline font-bold">{certificateData?.originAddress || '_______________'}</span>. That I am transporting the following forest products gathered from <span className="underline font-bold">{certificateData?.originAddress || '_______________'}</span>, described as follows, to wit:
            </p>
         </div>

         {/* Details List */}
         <div className="mb-10 pl-8">
            <p>1. Kind, species, quantity & vol. of forest products: <span className="underline">
               {(() => {
                  const volume = certificateData?.forestProducts?.volume;
                  const cubicMeters = volume?.cubicMeters !== undefined && volume?.cubicMeters !== null
                     ? Number(volume.cubicMeters).toFixed(2)
                     : '0.00';
                  const boardFeet = volume?.boardFeet !== undefined && volume?.boardFeet !== null
                     ? Number(volume.boardFeet).toFixed(1)
                     : '0.0';

                  return `${certificateData?.forestProducts?.species || ''} ${certificateData?.forestProducts?.kind || ''} ${certificateData?.forestProducts?.quantity?.value || ''} ${certificateData?.forestProducts?.quantity?.unit || ''}. ${cubicMeters}cu.m. (${boardFeet}bd ft)`;
               })()}
            </span></p>
            <p>2. Conveyance: <span className="underline">
               {certificateData?.conveyance && certificateData?.vehiclePlateNumber
                  ? `${certificateData.conveyance} with Plate No. ${certificateData.vehiclePlateNumber}`
                  : '_______________'}
            </span></p>
            <p>3. Driver: <span className="underline">
               {certificateData?.driverName || '_______________'}
            </span> from <span className="underline">
                  {certificateData?.driverAddress || '_______________'}
               </span></p>
            <p>4. Consignee/Destination: <span className="underline">
               {certificateData?.consignee || '_______________'}
            </span> / <span className="underline">
                  {certificateData?.destinationAddress || '_______________'}
               </span></p>
         </div>

         {/* Owner Signature */}
         <div className="text-right mb-12">
            <div className="inline-block text-center">
               <p className="font-bold">{certificateData?.name?.toUpperCase() || '_______________'}</p>
               <p className="text-center">Owner</p>
            </div>
         </div>

         {/* Certification Section */}
         <div className="mb-6">
            <h2 className="text-center font-bold mb-4">C E R T I F I C A T I O N</h2>
            <p className="mb-2">To whom it may concern:</p>
            <p className="text-justify indent-8 mb-4">
               This is to certify that the above-described forest products had been verified by this Office to have originated from the private land and is hereby allowed to be transported with the following particulars:
            </p>
         </div>

         {/* Route and Validity Details */}
         <div className="mb-6 pl-8">
            <div className="grid grid-cols-[120px_1fr] gap-2 mb-2">
               <div>Described Route</div>
               <div>
                  <div>From : <span className="underline">{certificateData?.originAddress || '_______________'}</span></div>
                  <div>To : <span className="underline">{certificateData?.destinationAddress || '_______________'}</span></div>
               </div>
            </div>
            <div className="grid grid-cols-[120px_1fr] gap-2 mb-2">
               <div>Validity Date</div>
               <div>
                  <div>From : <span className="underline">{formatDate(certificate?.dateIssued) || '_______________'}</span></div>
                  <div>To : <span className="underline">{formatDate(certificate?.expiryDate) || '_______________'}</span></div>
               </div>
            </div>
            <div className="grid grid-cols-[120px_1fr] gap-2">
               <div>Others</div>
               <div>: Please see attached copy of the Private Land Timber Permit from DENR MIMAROPA Region</div>
            </div>
         </div>

         {/* Payment Details */}
         <div className="mb-8">
            <p className="text-justify mb-4">
               Certification and inspection fees in the amount of Php <span className="underline">{orderOfPayment?.officialReceipt?.amount?.toFixed(2) || '_______________'}</span> in favor of the Department and Environment and Natural Resources were paid under Official Receipt No. <span className="underline">{orderOfPayment?.officialReceipt?.orNumber || '_______________'}</span> dated <span className="underline">{formatDate(orderOfPayment?.officialReceipt?.dateIssued) || '_______________'}</span>.
            </p>
         </div>

         {/* Signatures Section */}
         <div className="mb-12">
            {/* Scaled by Section */}
            <div className="mb-16">
               <p>Scaled by:</p>
               <div className="mt-2 text-left">
                  <div className="inline-block text-center">
                     <div className="flex justify-center">
                        {renderSignature(certificate?.forestRangerSignature)}
                     </div>
                     <p className="font-bold">MICHAEL JOSEPH A. MARANAN</p>
                     <p className="text-center">Forest Ranger</p>
                  </div>
               </div>
            </div>

            {/* Attested by Section */}
            <div>
               <p className="text-right mr-44">Attested by:</p>
               <div className="mt-8 flex flex-col items-end">
                  <div className="inline-block text-center">
                     <div className="flex justify-center">
                        {renderSignature(certificate?.chiefTSDSignature)}
                     </div>
                     <p className="font-bold">Engr. CYNTHIA U. LOZANO</p>
                     <p>Chief, Technical Services Division</p>
                  </div>
               </div>
            </div>
         </div>

         {/* Sworn Statement */}
         <div className="mb-12">
            <p className="mb-12">
               SUBSCRIBED AND SWORN to before me this <span className="underline">____</span> day of <span className="underline">________</span> at Boac Marinduque.
            </p>
            <div className="text-right">
               <div className="inline-block text-center">
                  <div className="flex justify-center w-full">
                     {renderSignature(certificate?.penrCenrOfficerSignature)}
                  </div>
                  <p className="font-bold">IMELDA M. DIAZ</p>
                  <p className="underline">OIC, PENR Officer</p>
                  <p>Authorized Person to Administer Oath</p>
               </div>
            </div>
         </div>

         {/* Footer */}
         <div className="text-center text-xs">
            <p>Capitol Compound, Barangay Bangbangalon, Boac, Marinduque</p>
            <p>Telephone Nos.: (042) 332-1490/(042) 332-0727/(042) 332-1913; VOIP: 2705</p>
            <p>Website: https://penromarinduque.gov.ph/</p>
            <p>Email: penromarinduque@denr.gov.ph</p>
         </div>

         {/* QR Code - Hidden when printing if specified */}
         <div className={`absolute top-4 right-4 ${hiddenOnPrint.includes('qr-code') ? 'no-print' : ''}`}>
            <QRCode
               value={qrValue}
               size={80}
               style={{ height: "auto", maxWidth: "100%", width: "100%" }}
               viewBox={`0 0 80 80`}
            />
            <p className="text-xs mt-1 text-center">Scan to verify</p>
         </div>
      </div>
   );
});

COVCertificateTemplate.displayName = 'COVCertificateTemplate';

export default COVCertificateTemplate;
